import { TripRef } from "@/types/trip"

import { nowMillis } from "@/lib/date-utils"
import { webStorage } from "@/lib/web-storage"

/** What a form asked a new chat to open with. */
export type ChatKickoff =
  | { kickoffContext: string; entityRefs?: string[]; tripRef?: TripRef }
  | { actionPillRef: string }

export type PendingChatKickoff = { kickoff: ChatKickoff; createdAt: number }

const STORAGE_KEY = "mindtrip.pending_chat_kickoff" as const
const MAX_AGE_MS = 24 * 60 * 60 * 1000

function isChatKickoff(value: unknown): value is ChatKickoff {
  if (!value || typeof value !== "object") return false
  return (
    ("kickoffContext" in value && typeof value.kickoffContext === "string") ||
    ("actionPillRef" in value && typeof value.actionPillRef === "string")
  )
}

function isPendingChatKickoff(value: unknown): value is PendingChatKickoff {
  return (
    !!value &&
    typeof value === "object" &&
    "kickoff" in value &&
    isChatKickoff(value.kickoff) &&
    "createdAt" in value &&
    typeof value.createdAt === "number"
  )
}

/**
 * Keeps a kickoff the sign-up gate interrupted across the reload that
 * follows signing in.
 */
export function setPendingChatKickoff(kickoff: ChatKickoff) {
  webStorage.setJSON(
    STORAGE_KEY,
    { kickoff, createdAt: nowMillis() },
    { type: "session" }
  )
}

export function getPendingChatKickoff(): PendingChatKickoff | null {
  const stored = webStorage.tryGetJSON<unknown>(STORAGE_KEY, {
    type: "session",
  })
  if (stored === null) return null
  if (
    !isPendingChatKickoff(stored) ||
    nowMillis() - stored.createdAt > MAX_AGE_MS
  ) {
    clearPendingChatKickoff()
    return null
  }
  return stored
}

export function clearPendingChatKickoff() {
  webStorage.removeItem(STORAGE_KEY, { type: "session" })
}
